/* Choosing a registered sentence, and filling its blanks.
 *
 * WHY THIS EXISTS AT ALL. SMS is not a text box. The carrier compares what
 * was sent against a registered template and rejects anything that does not
 * match — and that single rejection is the majority of all SMS failures on
 * the platform, almost always the right words sent under the wrong template.
 *
 * So a composer that offered a free-text box and then "pick a template"
 * would be lying: the words typed are not the words that go out. Instead the
 * admin picks the sentence first and fills in what changes.
 *
 * THE CHURCH'S NAME IS NEVER ASKED FOR. Every shared template ends with it,
 * filled in from whoever is sending — that is exactly what lets one approval
 * serve every church. Asking somebody to type their own church's name would
 * be asking them for something already known, and a typo would produce a
 * message the carrier refuses.
 */
import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, Check, Loader2, Lock } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Field } from '@/components/ui/field'
import { useMessageTemplates, usePreviewTemplate } from '@/api/queries'
import { cn } from '@/lib/utils'
import type { MessageTemplate } from '@/types/api'

/** What a blank is called, in an admin's language rather than the carrier's.
 *  Unknown names fall back to the name itself, so a template registered
 *  later still produces a usable form. */
const LABELS: Record<string, string> = {
  place: 'Where',
  day: 'Which day',
  time: 'What time',
  event: 'What is happening',
  message: 'What to say',
  reference: 'The reading',
  thought: 'The thought',
}

const HINTS: Record<string, string> = {
  place: 'The main hall',
  day: 'Sunday',
  time: '9:30',
  event: 'The harvest service',
  message: 'the church office is closed on Monday',
  reference: 'Psalm 23',
  thought: 'The Lord is my shepherd.',
}

function labelFor(name: string) {
  return LABELS[name] ?? name.charAt(0).toUpperCase() + name.slice(1)
}

export interface TemplateChoice {
  template: MessageTemplate
  values: Record<string, string>
  /** What members will actually receive, straight from the server. */
  rendered: string
}

export function TemplatePicker({
  churchId,
  channel,
  onChange,
}: {
  churchId: string
  channel: 'sms' | 'whatsapp'
  /** Null whenever the choice is incomplete, so the composer can refuse to
   *  send rather than sending something half-filled. */
  onChange: (choice: TemplateChoice | null) => void
}) {
  const templates = useMessageTemplates(channel)
  const preview = usePreviewTemplate(churchId)

  const [chosenId, setChosenId] = useState<string | null>(null)
  const [values, setValues] = useState<Record<string, string>>({})
  const [rendered, setRendered] = useState<string | null>(null)

  const rows = templates.data ?? []
  const chosen = rows.find((t) => t.id === chosenId) ?? null

  const complete = Boolean(
    chosen && chosen.asks_for.every((name) => (values[name] ?? '').trim()),
  )

  /* The preview comes from the server, not from filling the blanks here.
     Rendering it on the client would mean two implementations of the same
     substitution, and the one that matters is the one that will be sent. */
  const valuesKey = useMemo(() => JSON.stringify(values), [values])
  useEffect(() => {
    if (!chosen || !complete) {
      setRendered(null)
      onChange(null)
      return
    }
    let live = true
    const handle = setTimeout(() => {
      preview.mutate(
        { templateId: chosen.id, values },
        {
          onSuccess: (result) => {
            if (!live) return
            setRendered(result.rendered)
            onChange({ template: chosen, values, rendered: result.rendered })
          },
          onError: () => {
            if (!live) return
            setRendered(null)
            onChange(null)
          },
        },
      )
      /* Typed into, so debounced — a request per keystroke would be a
         request per keystroke for every admin in every church. */
    }, 250)
    return () => {
      live = false
      clearTimeout(handle)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenId, valuesKey, complete])

  if (templates.isLoading) {
    return (
      <Card className="p-s6 text-center text-ink-2">
        <Loader2 className="mx-auto h-5 w-5 animate-spin" aria-label="Loading" />
      </Card>
    )
  }

  if (!rows.length) {
    return (
      <Card className="p-s6 text-center text-base text-ink-2">
        There are no approved messages for this yet.
      </Card>
    )
  }

  return (
    <div className="space-y-s4">
      <div>
        <span className="mb-s2 block text-sm font-semibold text-ink-2">
          What are you telling them?
        </span>
        <div className="space-y-s2">
          {rows.map((template) => (
            <TemplateOption
              key={template.id}
              template={template}
              selected={template.id === chosenId}
              onSelect={() => {
                setChosenId(template.id)
                setValues({})
                setRendered(null)
              }}
            />
          ))}
        </div>
      </div>

      {chosen ? (
        <div className="space-y-s3">
          {chosen.asks_for.map((name) => (
            <Field
              key={name}
              label={labelFor(name)}
              value={values[name] ?? ''}
              onChange={(e) =>
                setValues((v) => ({
                  ...v,
                  [name]: e.target.value.slice(0, 160),
                }))
              }
              placeholder={HINTS[name] ?? ''}
              required
            />
          ))}

          {/* The whole point of the screen: the exact sentence, signed with
              this church's name, before anybody commits to sending it. */}
          <div>
            <span className="mb-s2 block text-sm font-semibold text-ink-2">
              What they will receive
            </span>
            <div
              className={cn(
                'rounded border p-s3 text-base',
                rendered
                  ? 'border-line-2 bg-surface-2'
                  : 'border-dashed border-line-2 text-ink-3',
              )}
            >
              {rendered ?? 'Fill in the blanks above and it will appear here.'}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function TemplateOption({
  template,
  selected,
  onSelect,
}: {
  template: MessageTemplate
  selected: boolean
  onSelect: () => void
}) {
  /* UNREGISTERED ONES ARE SHOWN, NOT HIDDEN. Hiding them would make the
     screen read as though this is all there is; showing them greyed says
     what is coming and why it cannot be used yet. */
  const locked = !template.is_registered

  return (
    <button
      type="button"
      onClick={locked ? undefined : onSelect}
      disabled={locked}
      aria-pressed={selected}
      className={cn(
        'flex w-full items-start gap-s3 rounded border p-s3 text-left transition-colors',
        locked
          ? 'cursor-default border-line-2 opacity-60'
          : selected
            ? 'border-brand bg-brand-softer'
            : 'border-line-2 hover:bg-hover',
      )}
    >
      <span
        className={cn(
          'mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-pill border',
          selected ? 'border-brand bg-brand text-ink-on-brand' : 'border-line',
        )}
        aria-hidden
      >
        {selected ? <Check className="h-3 w-3" /> : null}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-s2">
          <span className="font-semibold">{template.name}</span>
          {locked ? (
            <span className="inline-flex items-center gap-1 rounded-pill bg-surface-2 px-s2 py-0.5 text-xs font-bold text-ink-2">
              <Lock className="h-3 w-3" aria-hidden />
              Not approved yet
            </span>
          ) : null}
        </span>
        {template.description ? (
          <span className="mt-0.5 block text-sm text-ink-2">
            {template.description}
          </span>
        ) : null}
      </span>
    </button>
  )
}

/** Shown when a regulated channel is ticked but nothing can be sent on it.
 *  Says what is missing rather than simply refusing. */
export function TemplateNotice({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-start gap-s2 rounded-md bg-warning-soft px-s3 py-s2 text-base text-warning-ink">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      {children}
    </p>
  )
}
